import { z } from 'zod';
import { UserSummary } from './admin';
import { Id, IsoDate } from './common';
import { Intent, ProductType, ScoreAxis } from './enums';

/**
 * Configuration versionnée du scoring (`scoring_configs.weights` + `params`, `scoring.md`,
 * `api.md` § Configuration et gouvernance). Les formules sont fixes ; seuls poids, ancres,
 * multiplicateurs, seuils, plafonds et listes sont configurables. Schéma de données pur, partagé
 * par `core` (qui ajoute les contrôles sémantiques dans `validateConfig`) et par l'UI
 * (`ParamsEditor`).
 */

const unit = z.number().min(0).max(1);
const positive = z.number().positive();
const nonNegative = z.number().min(0);

/** Tolérance sur la somme des poids (`validation.weightsSum`). */
export const WEIGHTS_SUM_TOLERANCE = 0.001;
export const SCORING_NOTES_MAX_LENGTH = 2000;

/** `z.record` sur un enum est exhaustif : les sept axes sont obligatoires. */
export const ScoringWeights = z.record(ScoreAxis, unit);
export type ScoringWeights = z.infer<typeof ScoringWeights>;

export const VolumeAnchors = z.object({ low: positive, high: positive });
export type VolumeAnchors = z.infer<typeof VolumeAnchors>;

export const ScanCaps = z.object({
  triage: z.number().int().min(0),
  serp_clusters: z.number().int().min(0),
  deep: z.number().int().min(0),
  cost_usd: nonNegative,
});
export type ScanCaps = z.infer<typeof ScanCaps>;

export const ConfidenceParams = z.object({
  /** Paliers `[keyword_count_min, bonus]` ; le palier le plus haut atteint s'applique. */
  keywords: z.array(z.tuple([z.number().int().min(0), unit])),
  /** Bonus historique complet (`history_months ≥ history_months_full`). */
  history: unit,
  history_months_full: z.number().int().min(1),
  /** Bonus de stabilité (`cv_monthly < stable_cv_max`), conditionné à l'historique complet. */
  stability: unit,
  stable_cv_max: positive,
  serp_one: unit,
  serp_two: unit,
  serp_overlap_min: unit,
  llm_base: unit,
  llm_gain: unit,
  human_confirmed: unit,
  human_engaged: unit,
  experiment: unit,
  blocking_unknown: unit,
  blocking_unknown_cap: unit,
  heterogeneous: unit,
  brand_ratio_min: unit,
  brand_penalty: unit,
  bands: z.object({ low: unit, high: unit }),
});
export type ConfidenceParams = z.infer<typeof ConfidenceParams>;

/**
 * Seuils des portes d'éligibilité G1–G7 (`scoring.md` § 2). Les règles sont fixes ; seuls les
 * seuils sont configurables. `min_volume` est indexé par marché (mêmes valeurs que
 * `markets.min_volume`, qui prime quand la phase le fournit).
 */
export const GateParams = z.object({
  /** G2 : `volume_sum < min_volume[market]`, par marché. */
  min_volume: z.record(z.string(), nonNegative),
  /** G1 : part du volume touchée par un terme dur à partir de laquelle le cluster est exclu. */
  exclusion_volume_share: unit,
  /** Part du volume touchée par un terme `is_soft` à partir de laquelle le drapeau est posé. */
  soft_flag_volume_share: unit,
  /** G2 : une enchère haute ≥ ce montant (USD) sauve un cluster à faible volume. */
  low_demand_bid_escape_usd: nonNegative,
  /** G3 : `question_ratio + informational_ratio ≥ informational_ratio`. */
  informational_ratio: unit,
  /** G3 : `task_verb_ratio + modifier_ratio < task_or_modifier_max`. */
  task_or_modifier_max: unit,
  /** G3 : une enchère haute ≥ ce montant (USD) sauve un cluster informationnel. */
  informational_bid_escape_usd: nonNegative,
  /** G4 : `news_local_ratio ≥ news_local_ratio`. */
  news_local_ratio: unit,
  /** G5 : volume minimal d'un mot-clé testable. */
  untestable_min_volume: nonNegative,
  /** G7 : `brand_ratio ≥ brand_ratio` après SERP. */
  brand_ratio: unit,
});
export type GateParams = z.infer<typeof GateParams>;

export const ScoringParams = z.object({
  volume_anchors: z.record(z.string(), VolumeAnchors),
  cpc_norm: z.object({ offset: z.number(), divisor: positive }),
  trend_gain: nonNegative,
  seasonality_divisor: positive,
  seasonality_cap: unit,
  intent_multipliers: z.record(Intent, unit),
  product_type_multipliers: z.record(ProductType, unit),
  /** Part LLM dans le mélange métriques dures / analyse profonde de chaque axe. */
  blend: z.object({ A_deep: unit, B_deep: unit, C_deep: unit, D_deep: unit }),
  fallbacks: z.object({
    D_no_serp: unit,
    E_no_deep: unit,
    F_f2_no_serp: unit,
    F_f3_no_deep: unit,
    G_no_deep: unit,
  }),
  f1: z.object({ start: nonNegative, span: positive, floor: unit }),
  f2: z.object({ open: nonNegative, span: positive }),
  ads_norm_divisor: positive,
  risk_flag_weight: unit,
  thresholds: z.object({
    opportunity_min_score: z.number().min(0).max(100),
    review_priority_score: z.number().min(0).max(100),
    triage_min_score: z.number().min(0).max(10),
  }),
  default_caps: ScanCaps,
  serp_per_cluster: z.number().int().min(1),
  serp_max_age_days: z.number().int().min(1),
  theta_assign: unit,
  confidence: ConfidenceParams,
  /** Plateformes dominantes (suffixes de domaine) comptées dans `big_brand_ratio`. */
  big_platforms: z.array(z.string().min(1)),
  gates: GateParams,
});
export type ScoringParams = z.infer<typeof ScoringParams>;

export const ScoringConfig = z.object({
  version: z.number().int().positive(),
  weights: ScoringWeights,
  params: ScoringParams,
});
export type ScoringConfig = z.infer<typeof ScoringConfig>;

/** Somme des poids A–G. */
export function weightsSum(weights: Partial<Record<ScoreAxis, number>>): number {
  return SCORE_AXES_LIST.reduce((acc, axis) => acc + (weights[axis] ?? 0), 0);
}
const SCORE_AXES_LIST = ScoreAxis.options;

export function weightsSumIsValid(weights: Partial<Record<ScoreAxis, number>>): boolean {
  return Math.abs(weightsSum(weights) - 1) <= WEIGHTS_SUM_TOLERANCE;
}

/** Ligne de `GET /scoring-configs`. */
export const ScoringConfigRow = z.object({
  id: Id,
  version: z.number().int(),
  weights: ScoringWeights,
  params: ScoringParams,
  isActive: z.boolean(),
  notes: z.string(),
  createdBy: UserSummary.nullable(),
  createdAt: IsoDate,
});
export type ScoringConfigRow = z.infer<typeof ScoringConfigRow>;

export const ScoringConfigsResponse = z.object({ items: z.array(ScoringConfigRow) });
export type ScoringConfigsResponse = z.infer<typeof ScoringConfigsResponse>;

/** `POST /scoring-configs` : nouvelle version inactive ; somme des poids = 1 ± 0,001. */
export const CreateScoringConfigBody = z
  .object({
    weights: ScoringWeights,
    params: ScoringParams,
    notes: z
      .string()
      .trim()
      .min(1, 'validation.required')
      .max(SCORING_NOTES_MAX_LENGTH, 'validation.maxLength'),
  })
  .superRefine((value, ctx) => {
    if (!weightsSumIsValid(value.weights)) {
      ctx.addIssue({ code: 'custom', path: ['weights'], message: 'validation.weightsSum' });
    }
  });
export type CreateScoringConfigBody = z.infer<typeof CreateScoringConfigBody>;

/** `POST /scoring-configs/rescore` : version à utiliser (défaut : la version active). */
export const RescoreBody = z.object({ configVersion: z.number().int().positive().optional() });
export type RescoreBody = z.infer<typeof RescoreBody>;
